(() => {
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const isVisibleFocusable = (item) => !item.hidden && !item.closest('[hidden]');
  const marketSelectionSteps = {
    country: {
      title: '¿Dónde quieres comprar?',
      support: 'Selecciona el país desde el que realizarás tu compra.'
    },
    language: {
      title: '¿En qué idioma prefieres navegar?',
      support: ''
    }
  };
  const marketSelectionStorageKey = 'eusari.market-selection.v1.completed';
  const marketReturnPathCookieKey = 'eusari.market-return-path.v1';
  const marketSelectionAutoOpenFlag = '__eusariMarketSelectionPromptAutoOpenedV1';
  const marketRequiredParam = 'menotest_market';
  const marketRequiredValue = 'required';

  const encodeMarketReturnPath = (path) => encodeURIComponent(path);
  const readMarketReturnPathCookie = (cookie) => {
    const prefix = `${marketReturnPathCookieKey}=`;
    const value = String(cookie || '').split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
    if (!value) return null;
    try {
      return decodeURIComponent(value.slice(prefix.length));
    } catch (error) {
      return null;
    }
  };
  const isLocalizedStorefrontPath = (path, origin) => {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return false;
    try {
      const target = new URL(path, origin);
      return target.origin === origin
        && /^\/[a-z]{2}(?:-[a-z]{2})?(?:\/|$)/i.test(target.pathname)
        && target.pathname !== '/';
    } catch (error) {
      return false;
    }
  };
  const localizedMarketHomepagePath = (pathname) => {
    const match = typeof pathname === 'string'
      ? pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i)
      : null;
    if (!match) return null;
    const marketPrefix = match[1].toLowerCase();
    return marketPrefix === 'es' ? '/' : `/${marketPrefix}/`;
  };
  const rootMarketRedirectTarget = ({ pathname, search = '', cookie, origin }) => {
    if (pathname !== '/' || new URLSearchParams(search).has('preview_theme_id')) return null;
    const path = readMarketReturnPathCookie(cookie);
    if (!isLocalizedStorefrontPath(path, origin)) return null;
    const target = new URL(path, origin);
    const homepagePath = localizedMarketHomepagePath(target.pathname);
    if (!homepagePath || homepagePath === pathname) return null;
    const requestSearch = search.startsWith('?') ? search.slice(1) : search;
    return `${homepagePath}${requestSearch ? `?${requestSearch}` : ''}`;
  };
  const clearMarketReturnPathCookie = (documentRef = document) => {
    try {
      documentRef.cookie = `${marketReturnPathCookieKey}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
      return true;
    } catch (error) {
      return false;
    }
  };
  const persistMarketReturnPathCookie = (path, { origin = window.location.origin, documentRef = document } = {}) => {
    if (path === '/') return clearMarketReturnPathCookie(documentRef);
    if (!isLocalizedStorefrontPath(path, origin)) return false;
    try {
      documentRef.cookie = `${marketReturnPathCookieKey}=${encodeMarketReturnPath(path)}; Path=/; Max-Age=2592000; SameSite=Lax; Secure`;
      return true;
    } catch (error) {
      return false;
    }
  };

  const readStoredMarketSelection = (storage) => {
    try {
      return storage?.getItem(marketSelectionStorageKey);
    } catch (error) {
      return null;
    }
  };
  const persistMarketSelection = (storage) => {
    try {
      storage?.setItem(marketSelectionStorageKey, 'true');
      return true;
    } catch (error) {
      return false;
    }
  };
  const shouldAutoOpenMarketSelection = ({ storage, designMode, automaticOpenClaimed }) => {
    return !designMode && !automaticOpenClaimed && readStoredMarketSelection(storage) !== 'true';
  };
  // Arrival from the Menotest result pages carries `menotest_market=required`.
  // For that arrival the remembered completion is ignored: the shopper has to
  // choose a country explicitly before the destination can be reached.
  const marketSelectionRequired = (search) => {
    try {
      return new URLSearchParams(search || '').get(marketRequiredParam) === marketRequiredValue;
    } catch (error) {
      return false;
    }
  };
  const shouldForceMarketSelection = ({ search, storedSelection } = {}) => {
    if (!marketSelectionRequired(search)) return false;
    // A remembered completion never disables the explicit selection for a
    // triggered arrival, so the stored value is deliberately not consulted.
    return true;
  };
  const stripMarketRequiredParam = (search) => {
    try {
      const params = new URLSearchParams(search || '');
      params.delete(marketRequiredParam);
      return params.toString();
    } catch (error) {
      return '';
    }
  };
  const stripMarketRequiredFromPath = (value) => {
    if (typeof value !== 'string' || !value) return value;
    const separatorIndex = value.indexOf('?');
    if (separatorIndex === -1) return value;
    const pathPart = value.slice(0, separatorIndex);
    const params = new URLSearchParams(value.slice(separatorIndex + 1));
    params.delete(marketRequiredParam);
    const search = params.toString();
    return search ? `${pathPart}?${search}` : pathPart;
  };
  const applyForcedCountryState = (controls) => {
    (controls || []).forEach((control) => {
      if (control) control.checked = false;
    });
    return false;
  };
  const hasExplicitCountryChoice = (controls) => (controls || []).some((control) => control && control.checked === true);
  const createMarketSelectionGate = ({ required, onConsume, onBlockedDismiss } = {}) => {
    let consumed = false;
    const isActive = () => Boolean(required) && !consumed;
    return {
      isActive,
      requiresExplicitCountry: isActive,
      // Returns true when the dismissal was blocked and the modal must stay open.
      handleDismissAttempt: (reason) => {
        if (!isActive()) return false;
        if (typeof onBlockedDismiss === 'function') onBlockedDismiss(reason);
        return true;
      },
      consume: () => {
        if (!isActive()) return false;
        consumed = true;
        if (typeof onConsume === 'function') onConsume();
        return true;
      }
    };
  };
  // Hands the market change over to the native Shopify localization form.
  // The forced market marker is never cleared here: the current URL keeps it
  // until the native navigation succeeds, and the sanitized destination drops
  // it so the redirected page does not ask for the market again.
  const submitNativeLocalizationForm = ({ form, submitForm, onSuccess, onFailure } = {}) => {
    if (!form || typeof submitForm !== 'function') return false;
    try {
      submitForm(form);
      if (typeof onSuccess === 'function') onSuccess();
      return true;
    } catch (error) {
      if (typeof onFailure === 'function') onFailure(error);
      return false;
    }
  };
  const marketProductHandles = {
    AR: 'menoplay-el-suplemento-para-la-menopausia-que-te-mereces-ar',
    CA: 'menoplay-the-menopause-supplement-you-deserve-ca',
    CL: 'menoplay-el-suplemento-para-la-menopausia-que-te-mereces-cl',
    CO: 'menoplay-el-suplemento-para-la-menopausia-que-te-mereces-co',
    ES: 'menoplay-el-suplemento-para-la-menopausia-que-te-mereces-es',
    MX: 'menoplay-el-suplemento-para-la-menopausia-que-te-mereces-mx',
    US: 'menoplay-menopause-supplement'
  };
  const menoplayHandles = new Set(Object.values(marketProductHandles));
  const marketLanguages = {
    AR: 'es',
    CA: 'en',
    CL: 'es',
    CO: 'es',
    ES: 'es',
    MX: 'es',
    US: 'en'
  };
  const marketHomepagePath = (countryCode, languageCode) => {
    const market = countryCode?.toUpperCase();
    if (!market) return null;

    const language = languageCode?.split('-')[0]?.toLowerCase() || marketLanguages[market] || 'en';
    if (market === 'US') return language === 'es' ? '/' : `/${language}/`;
    return `/${language}-${market.toLowerCase()}/`;
  };
  const marketReturnPath = (countryCode, languageCode) => {
    if (!countryCode) return null;

    const currentUrl = new URL(window.location.href);
    const currentLocale = currentUrl.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    const language = languageCode?.split('-')[0] || marketLanguages[countryCode] || currentLocale?.[1] || 'en';
    const marketPrefix = `${language.toLowerCase()}-${countryCode.toLowerCase()}`;
    let pathWithoutLocale = currentUrl.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/';
    const productMatch = pathWithoutLocale.match(/^\/products\/([^/]+)/i);
    const targetProductHandle = marketProductHandles[countryCode];
    if (productMatch && targetProductHandle && productMatch[1].toLowerCase().includes('menoplay')) {
      pathWithoutLocale = pathWithoutLocale.replace(/^\/products\/[^/]+/i, `/products/${targetProductHandle}`);
    }
    const isPrimaryUsMarket = countryCode === 'US';
    const localizedPath = isPrimaryUsMarket
      ? language.toLowerCase() === 'es'
        ? pathWithoutLocale
        : pathWithoutLocale === '/'
          ? `/${language.toLowerCase()}/`
          : `/${language.toLowerCase()}${pathWithoutLocale}`
      : pathWithoutLocale === '/'
        ? `/${marketPrefix}/`
        : `/${marketPrefix}${pathWithoutLocale}`;

    const searchParams = new URLSearchParams(currentUrl.search);
    const currentView = searchParams.get('view');
    const marketViews = {
      'menoplay-pdp': 'menoplay-pdp-us',
      'ruta-menopausia': 'ruta-menopausia-us',
      'ruta-peri': 'ruta-peri-us',
      'manten-bienestar': 'manten-bienestar-us'
    };
    if (currentView) {
      const baseView = currentView.replace(/-us$/i, '');
      if (marketViews[baseView]) {
        searchParams.set('view', countryCode === 'US' ? marketViews[baseView] : baseView);
      }
    }
    searchParams.delete('preview_theme_id');
    // The forced market marker never travels with the destination, otherwise the
    // redirected page would ask for the market again.
    searchParams.delete(marketRequiredParam);
    const search = searchParams.toString();

    return `${localizedPath}${search ? `?${search}` : ''}${currentUrl.hash}`;
  };

  const cartUrl = (path) => `${window.theme?.routes?.cart || '/cart'}${path}`;
  const sellingPlanId = (allocation) => allocation?.selling_plan?.id ?? allocation?.selling_plan_id;
  const targetMarketProductPath = (countryCode, languageCode, handle) => {
    return `/products/${handle}.js`;
  };
  const productRequestUrl = (path) => {
    const shop = window.Shopify?.shop;
    return shop && !window.location.hostname.endsWith('.myshopify.com')
      ? `https://${shop}${path}`
      : path;
  };
  const lineHandle = (line) => line?.product_handle || line?.handle || '';
  const isMenoPlayLine = (line) => menoplayHandles.has(lineHandle(line)) || /menoplay/i.test([
    lineHandle(line), line?.product_title, line?.title
  ].filter(Boolean).join(' '));

  const cartRequest = async (path, options) => {
    const response = await window.fetch(cartUrl(path), {
      credentials: 'same-origin',
      ...options
    });
    if (!response.ok) throw new Error('Unable to update the MenoPlay cart.');
    return response;
  };

  const removeCartLine = (key) => cartRequest('/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  });

  const migrateMenoPlayCart = async (countryCode, languageCode) => {
    const targetHandle = marketProductHandles[countryCode];
    const cart = await (await cartRequest('.js')).json();
    const menoplayLines = (cart.items || []).filter(isMenoPlayLine);

    if (!menoplayLines.length) return false;
    if (menoplayLines.length !== 1 || !targetHandle) {
      throw new Error('We could not safely update your MenoPlay cart for this market.');
    }

    const sourceLine = menoplayLines[0];
    if (lineHandle(sourceLine) === targetHandle) return false;

    const productPath = targetMarketProductPath(countryCode, languageCode, targetHandle);
    const productResponse = await window.fetch(productRequestUrl(productPath), { credentials: 'omit' });
    if (!productResponse.ok) throw new Error('The MenoPlay product for this market is unavailable.');
    const targetProduct = await productResponse.json();
    const targetVariant = (targetProduct.variants || []).find((variant) => variant.available);
    if (!targetVariant) throw new Error('The MenoPlay product for this market is unavailable.');

    const sourceSellingPlanId = sellingPlanId(sourceLine.selling_plan_allocation);
    const targetSellingPlans = targetVariant.selling_plan_allocations || [];
    const targetSellingPlan = sourceSellingPlanId
      ? targetSellingPlans.find((allocation) => String(sellingPlanId(allocation)) === String(sourceSellingPlanId)) || targetSellingPlans[0]
      : null;
    if (sourceSellingPlanId && !targetSellingPlan) {
      throw new Error('The MenoPlay subscription is unavailable for this market.');
    }

    const addPayload = { id: targetVariant.id, quantity: sourceLine.quantity };
    if (targetSellingPlan) addPayload.selling_plan = sellingPlanId(targetSellingPlan);

    const cartLimit = window.MenoPlayCartLimit;
    if (cartLimit?.registerVariantIds && cartLimit?.beginReplacement) {
      cartLimit.registerVariantIds([sourceLine.variant_id, targetVariant.id].join(','));
    }
    const sendReplacementAdd = cartLimit?.beginReplacement
      ? await cartLimit.beginReplacement(sourceLine.key, targetVariant.id)
      : window.fetch.bind(window);
    const addResponse = await sendReplacementAdd(cartUrl('/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addPayload)
    });
    if (!addResponse.ok) throw new Error('Unable to add the MenoPlay product for this market.');
    const addedLine = await addResponse.json();

    try {
      const cartAfterAdd = await (await cartRequest('.js')).json();
      const currentSourceLine = (cartAfterAdd.items || []).find((line) =>
        String(line.variant_id) === String(sourceLine.variant_id) &&
        (!sourceSellingPlanId || String(sellingPlanId(line.selling_plan_allocation)) === String(sourceSellingPlanId))
      );
      if (!currentSourceLine) {
        throw new Error('Unable to find the original MenoPlay cart line after the market update.');
      }
      await removeCartLine(currentSourceLine.key);
    } catch (error) {
      let replacementKey = addedLine?.key;
      if (!replacementKey) {
        const updatedCart = await (await cartRequest('.js')).json();
        const replacement = (updatedCart.items || []).find((line) =>
          String(line.variant_id) === String(targetVariant.id) &&
          String(sellingPlanId(line.selling_plan_allocation) || '') === String(addPayload.selling_plan || '')
        );
        replacementKey = replacement?.key;
      }
      if (!replacementKey) throw new Error('Unable to restore the cart after the market update failed.');
      try {
        await removeCartLine(replacementKey);
      } catch (rollbackError) {
        throw new Error('Unable to restore the cart after the market update failed.');
      }
      throw error;
    }

    return true;
  };

  // Kept available for focused cart-migration tests and other market controls.
  if (typeof window !== 'undefined') {
    window.MenoPlayMarketCartMigration = { migrate: migrateMenoPlayCart };
  }

  const getCardTitle = (control) => control.closest('.pref-card')?.querySelector('h3')?.textContent.trim().toLowerCase() || '';

  const getPreference = (control) => {
    const title = getCardTitle(control);
    const label = control.closest('label')?.textContent.trim().toLowerCase() || '';
    if (/texto|tama\u00f1o/.test(title)) {
      if (/extra|muy grande|x-large/.test(label)) return ['text', 'extra-large'];
      return ['text', /grande/.test(label) ? 'large' : 'default'];
    }
    if (/contraste/.test(title)) return ['contrast', control.type === 'radio' ? (/alto/.test(label) ? 'high' : 'default') : (control.checked ? 'high' : 'default')];
    if (/movimiento|animaci/.test(title)) return ['motion', control.type === 'radio' ? (/reduc/.test(label) ? 'reduced' : 'system') : (control.checked ? 'reduced' : 'system')];
    if (/espaciado|controles|espacio/.test(title)) return ['spacing', control.type === 'radio' ? (/grande|amplio/.test(label) ? 'comfortable' : 'default') : (control.checked ? 'comfortable' : 'default')];
    return null;
  };

  const init = (root) => {
    if (!root || root.dataset.referenceHeaderReady) return;
    root.dataset.referenceHeaderReady = 'true';

    const sectionId = root.dataset.sectionId;
    const modal = document.querySelector(`[data-eusari-header-modal="${sectionId}"]`);
    const bottomNav = document.querySelector(`[data-eusari-mobile-bottom-nav="${sectionId}"]`);
    const overlay = document.querySelector(`[data-sheet-overlay="${sectionId}"]`);
    const sheets = [...document.querySelectorAll(`[data-eusari-section-id="${sectionId}"][data-eusari-sheet]`)];
    const sheetTriggers = [...document.querySelectorAll(`[data-sheet-trigger][aria-controls$="-${sectionId}"]`)];
    const modalButton = root.querySelector('.top-bar-pill');
     const modalClose = modal?.querySelector('.pref-modal-close');
     const modalSave = modal?.querySelector('#btnSavePref-' + sectionId);
     const modalReset = modal?.querySelector('#btnResetPref-' + sectionId);
     const modalContinue = modal?.querySelector('[data-market-step-continue]');
     const modalBack = modal?.querySelector('[data-market-step-back]');
     const modalStepTitle = modal?.querySelector('[data-market-step-title]');
     const modalStepSupport = modal?.querySelector('[data-market-step-support]');
     const modalForm = modal?.querySelector('form');
     const modalTabs = [...(modal?.querySelectorAll('[data-preference-tab]') || [])];
     const modalPanels = [...(modal?.querySelectorAll('[data-preference-panel]') || [])];
     const accessibilityStatus = modal?.querySelector('[data-accessibility-status]');
    const countryRadios = [...(modalForm?.querySelectorAll('input[name="eusari-country"]') || [])];
    const marketRequiredArrival = marketSelectionRequired(window.location.search)
      || Boolean(window.EusariMenotestJourney?.isMarketRequired?.());
    const marketGate = createMarketSelectionGate({
      required: marketRequiredArrival,
      onConsume: () => {},
      onBlockedDismiss: () => {}
    });
    let sheetOpen = false;
    let activeSheet = null;
    let modalOpen = false;
    let activeMarketSelectionStep = 'country';
    let lastModalFocus;
    let lastSheetFocus;
    let lastScrollTop = 0;

    const setMenuIcon = (open) => {
      const icon = sheetTriggers.find((trigger) => trigger.dataset.sheetTrigger === 'menu')?.querySelector('i');
      if (!icon) return;
      icon.classList.toggle('ph-squares-four', !open);
      icon.classList.toggle('ph-x', open);
    };

     const setModalSaveState = (saving) => {
       if (!modalSave) return;
       if (!modalSave.dataset.defaultLabel) modalSave.dataset.defaultLabel = modalSave.textContent.trim();
       modalSave.disabled = saving;
       modalSave.setAttribute('aria-busy', String(saving));
       modalSave.textContent = saving ? 'Guardando cambios…' : modalSave.dataset.defaultLabel;
     };

     const syncAccessibilityControls = () => {
       const state = window.EusariAccessibility?.getState();
       if (!modal || !state) return;
       modal.querySelectorAll('.pref-radio-input, .pref-toggle-input').forEach((control) => {
        const preference = getPreference(control);
        if (!preference) return;
        const [name, value] = preference;
        control.checked = state[name] === value;
      });
    };

    const announceAccessibilityUpdate = (message) => {
      if (!accessibilityStatus) return;
      accessibilityStatus.textContent = '';
      requestAnimationFrame(() => { accessibilityStatus.textContent = message; });
    };

    const updateAccessibilityPreference = (control) => {
      const preference = getPreference(control);
      if (!preference || !window.EusariAccessibility) return;
      const [name, value] = preference;
      window.EusariAccessibility.setPreference(name, value);
      announceAccessibilityUpdate('Preferencia de accesibilidad actualizada.');
    };

    const syncScrollLock = () => document.body.classList.toggle('eusari-header-scroll-lock', sheetOpen || modalOpen);
    const closeSheets = (returnFocus = false) => {
      if (!sheetOpen) return;
      sheets.forEach((sheet) => {
        sheet.classList.remove('is-active');
        sheet.setAttribute('aria-hidden', 'true');
        sheet.setAttribute('inert', '');
      });
      sheetTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      overlay?.classList.remove('is-active');
      overlay?.setAttribute('aria-hidden', 'true');
      sheetOpen = false;
      activeSheet = null;
      setMenuIcon(false);
      syncScrollLock();
      if (returnFocus && lastSheetFocus instanceof HTMLElement) lastSheetFocus.focus();
    };
    const openSheet = (name, trigger) => {
      const sheet = sheets.find((item) => item.dataset.eusariSheet === name);
      if (!sheet) return;
      if (sheetOpen) closeSheets(false);
      lastSheetFocus = trigger || document.activeElement;
      sheetOpen = true;
      activeSheet = sheet;
      setMenuIcon(name === 'menu');
      sheet.classList.add('is-active');
      sheet.setAttribute('aria-hidden', 'false');
      sheet.removeAttribute('inert');
      overlay?.classList.add('is-active');
      overlay?.setAttribute('aria-hidden', 'false');
      sheetTriggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
      syncScrollLock();
      requestAnimationFrame(() => sheet.querySelector('[data-sheet-close], [role="tab"], a[href]')?.focus());
    };
    const getMarketSelectionStorage = () => {
      try {
        return window.localStorage;
      } catch (error) {
        return null;
      }
    };
    const markMarketSelectionComplete = () => persistMarketSelection(getMarketSelectionStorage());
    const requireExplicitCountryChoice = () => {
      if (!marketGate.requiresExplicitCountry()) return true;
      return hasExplicitCountryChoice(countryRadios);
    };
    const showMarketRequirementNotice = () => {
      if (!modalForm) return;
      let notice = modalForm.querySelector('[data-market-required-error]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.marketRequiredError = 'true';
        notice.setAttribute('role', 'alert');
        modalForm.prepend(notice);
      }
      notice.textContent = 'Selecciona el país para continuar.';
      setMarketSelectionStep('country');
    };
    const setModalHeader = (title, support) => {
      if (modalStepTitle) modalStepTitle.textContent = title;
      if (modalStepSupport) {
        modalStepSupport.textContent = support;
        modalStepSupport.hidden = !support;
      }
    };
    const setMarketSelectionStep = (step) => {
      const content = marketSelectionSteps[step];
      if (!modal || !content) return;
      activeMarketSelectionStep = step;
      modal.querySelectorAll('[data-market-step]').forEach((element) => {
        element.hidden = element.dataset.marketStep !== step;
      });
      setModalHeader(content.title, content.support);
      if (modalContinue) modalContinue.hidden = step !== 'country';
      if (modalBack) modalBack.hidden = step !== 'language';
      if (modalSave) modalSave.hidden = step !== 'language';
    };
    const setModalTab = (name, focus = false) => {
      const selectedTab = modalTabs.find((tab) => tab.dataset.preferenceTab === name);
      if (!selectedTab) return;
      modalTabs.forEach((tab) => {
        const selected = tab === selectedTab;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      modalPanels.forEach((panel) => {
        const selected = panel.dataset.preferencePanel === name;
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });
      if (name === 'accessibility') {
        setModalHeader('Preferencias de accesibilidad', 'Los cambios se aplican automáticamente.');
      } else {
        const content = marketSelectionSteps[activeMarketSelectionStep];
        setModalHeader(content.title, content.support);
      }
      if (focus) selectedTab.focus();
    };

    const setModal = (open, returnFocus = false) => {
      if (!modal || !modalButton) return;
      modalOpen = open;
      modal.classList.toggle('is-open', open);
      modal.setAttribute('aria-hidden', String(!open));
      modalButton.setAttribute('aria-expanded', String(open));
      syncScrollLock();
      if (open) {
        lastModalFocus = document.activeElement;
        setModalTab('localization');
        setMarketSelectionStep('country');
        syncAccessibilityControls();
        modalClose?.focus();
      } else if (returnFocus && lastModalFocus instanceof HTMLElement) {
        lastModalFocus.focus();
      }
    };

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop <= 0) {
        root.classList.remove('is-hidden', 'is-scrolled');
        bottomNav?.classList.remove('is-hidden');
        lastScrollTop = 0;
        return;
      }
      if (window.matchMedia('(max-width: 63.9375rem)').matches) {
        if (scrollTop > lastScrollTop && scrollTop > 100) {
          root.classList.add('is-hidden');
          bottomNav?.classList.add('is-hidden');
        } else if (scrollTop < lastScrollTop - 5) {
          root.classList.remove('is-hidden');
          bottomNav?.classList.remove('is-hidden');
        }
      } else {
        root.classList.toggle('is-hidden', scrollTop > lastScrollTop && scrollTop > 120);
        bottomNav?.classList.toggle('is-hidden', scrollTop > lastScrollTop && scrollTop > 120);
      }
      root.classList.toggle('is-scrolled', scrollTop > 10);
      lastScrollTop = scrollTop;
    }, { passive: true });

    sheetTriggers.forEach((trigger) => trigger.addEventListener('click', () => {
      const name = trigger.dataset.sheetTrigger;
      openSheet(name, trigger);
    }));
    overlay?.addEventListener('click', () => closeSheets(true));
    const activateFormulaTab = (sheet, tab, focus = false) => {
      if (!tab) return;
      const name = tab.dataset.formulaTab;
      sheet.querySelectorAll('[data-formula-tab]').forEach((item) => {
        const selected = item === tab;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-selected', String(selected));
      });
      sheet.querySelectorAll('.sheet-tab-panel').forEach((panel) => {
        const selected = panel.id === `formula-panel-${name}-${sectionId}`;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
        panel.setAttribute('aria-hidden', String(!selected));
      });
      if (focus) tab.focus();
    };

    sheets.forEach((sheet) => {
      sheet.querySelectorAll('[data-sheet-close]').forEach((button) => button.addEventListener('click', () => closeSheets(true)));
      let startX = 0;
      let startY = 0;
      sheet.addEventListener('touchstart', (event) => {
        startX = event.touches[0]?.clientX || 0;
        startY = event.touches[0]?.clientY || 0;
      }, { passive: true });
      sheet.addEventListener('touchmove', (event) => {
        const deltaX = (event.touches[0]?.clientX || 0) - startX;
        const deltaY = (event.touches[0]?.clientY || 0) - startY;
        if (deltaY > 50 && deltaY > Math.abs(deltaX)) closeSheets(true);
      }, { passive: true });
      sheet.addEventListener('touchend', (event) => {
        if (sheet.dataset.eusariSheet !== 'formulas') return;
        const endX = event.changedTouches[0]?.clientX || startX;
        const endY = event.changedTouches[0]?.clientY || startY;
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const threshold = Math.max(40, Math.min(72, window.innerWidth * 0.15));
        if (Math.abs(deltaX) < threshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        const tabs = [...sheet.querySelectorAll('[data-formula-tab]')];
        const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        const nextIndex = deltaX < 0 ? currentIndex + 1 : currentIndex - 1;
        activateFormulaTab(sheet, tabs[nextIndex]);
      }, { passive: true });
      sheet.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeSheets(true);
          return;
        }
        if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && event.target.matches('[data-formula-tab]')) {
          const tabs = [...sheet.querySelectorAll('[data-formula-tab]')];
          const currentIndex = tabs.indexOf(event.target);
          const nextIndex = event.key === 'ArrowRight' ? currentIndex + 1 : currentIndex - 1;
          if (tabs[nextIndex]) {
            event.preventDefault();
            activateFormulaTab(sheet, tabs[nextIndex], true);
          }
          return;
        }
        if (event.key !== 'Tab') return;
        const items = [...sheet.querySelectorAll(focusable)].filter((item) => !item.hidden && !item.closest('[hidden]'));
        if (!items.length) return;
        if (event.shiftKey && document.activeElement === items[0]) {
          event.preventDefault();
          items.at(-1).focus();
        } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
          event.preventDefault();
          items[0].focus();
        }
      });
    });

    sheets.forEach((sheet) => sheet.querySelectorAll('.accordion-header').forEach((button) => button.addEventListener('click', () => {
      const item = button.closest('.accordion-item');
      const open = !item.classList.contains('is-open');
      item.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', String(open));
    })));
    sheets.forEach((sheet) => sheet.querySelectorAll('[data-formula-tab]').forEach((tab) => tab.addEventListener('click', () => activateFormulaTab(sheet, tab))));

    const formulaTargets = root.querySelectorAll('.hover-target');
    const formulaPanels = root.querySelectorAll('.formulas-dynamic-area');
    const selectFormula = (target) => {
      formulaTargets.forEach((item) => item.classList.toggle('is-selected', item === target));
      formulaPanels.forEach((panel) => panel.classList.toggle('is-active', panel.id === `panel-${target.dataset.target}`));
    };
    formulaTargets.forEach((target) => {
       target.addEventListener('mouseenter', () => selectFormula(target));
       target.addEventListener('click', () => selectFormula(target));
       target.addEventListener('focus', () => selectFormula(target));
    });

    root.querySelectorAll('.nav-item').forEach((item) => {
      const trigger = item.querySelector('button.nav-link');
      if (!trigger) return;
      const setExpanded = (expanded) => trigger.setAttribute('aria-expanded', String(expanded));
      item.addEventListener('mouseenter', () => setExpanded(true));
      item.addEventListener('mouseleave', () => setExpanded(false));
      item.addEventListener('focusin', () => setExpanded(true));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) setExpanded(false);
      });
      trigger.addEventListener('click', () => setExpanded(trigger.getAttribute('aria-expanded') !== 'true'));
    });

    const openMarketSelectionOnFirstVisit = () => {
      if (!shouldAutoOpenMarketSelection({
        storage: getMarketSelectionStorage(),
        designMode: Boolean(window.Shopify?.designMode),
        automaticOpenClaimed: Boolean(window[marketSelectionAutoOpenFlag])
      })) return;
      window[marketSelectionAutoOpenFlag] = true;
      setModal(true);
    };

    modalButton?.addEventListener('click', () => setModal(true));
    sheets.forEach((sheet) => sheet.querySelectorAll('[data-sheet-preferences]').forEach((button) => button.addEventListener('click', () => {
      closeSheets(false);
      sheetTriggers.find((trigger) => trigger.dataset.sheetTrigger === 'menu')?.focus();
      setModal(true);
    })));
    modalClose?.addEventListener('click', () => {
      if (marketGate.handleDismissAttempt('close')) return;
      markMarketSelectionComplete();
      setModal(false, true);
    });
    modalTabs.forEach((tab) => tab.addEventListener('click', () => setModalTab(tab.dataset.preferenceTab, true)));
    modal?.addEventListener('keydown', (event) => {
      if (!event.target.matches('[data-preference-tab]')) return;
      const currentIndex = modalTabs.indexOf(event.target);
      let nextIndex = currentIndex;
      const navigationKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!navigationKeys.includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % modalTabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + modalTabs.length) % modalTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = modalTabs.length - 1;
      if (nextIndex !== currentIndex) setModalTab(modalTabs[nextIndex].dataset.preferenceTab, true);
    });
    modal?.addEventListener('click', (event) => {
      if (event.target === modal) {
        if (marketGate.handleDismissAttempt('backdrop')) return;
        markMarketSelectionComplete();
        setModal(false, true);
      }
    });
    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (marketGate.handleDismissAttempt('escape')) return;
        markMarketSelectionComplete();
        setModal(false, true);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...modal.querySelectorAll(focusable)].filter(isVisibleFocusable);
      if (!items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        items.at(-1).focus();
      } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
        event.preventDefault();
        items[0].focus();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sheetOpen) closeSheets(true);
    });
    modal?.addEventListener('change', (event) => {
      const control = event.target;
      if (!(control instanceof HTMLInputElement)) return;
      updateAccessibilityPreference(control);
    });
    document.addEventListener('eusari:a11ychange', syncAccessibilityControls);

    const preserveMarketReturnPath = () => {
      if (!modalForm) return null;

      const countrySelect = modalForm.querySelector('select[name="country_code"]');
      const localeSelect = modalForm.querySelector('select[name="language_code"]');
      const countryCode = countrySelect?.value?.toUpperCase();
       const effectiveLanguage = localeSelect?.value || marketLanguages[countryCode];
       const returnPath = marketReturnPath(countryCode, effectiveLanguage);
      if (!returnPath) return null;

      let returnToInput = modalForm.querySelector('input[name="return_to"]');
      if (!returnToInput) {
        returnToInput = document.createElement('input');
        returnToInput.type = 'hidden';
        returnToInput.name = 'return_to';
        modalForm.appendChild(returnToInput);
      }
      returnToInput.value = returnPath;
      return returnPath;    };

    const showMarketMigrationError = (error) => {
      let alert = modalForm?.querySelector('[data-market-migration-error]');
      if (!alert && modalForm) {
        alert = document.createElement('p');
        alert.dataset.marketMigrationError = 'true';
        alert.setAttribute('role', 'alert');
        modalForm.prepend(alert);
      }
      if (alert) alert.textContent = error?.message || 'Unable to update your cart for the selected market.';
    };

     const submitLocalizationForm = async (event) => {
       event?.preventDefault();
       if (!modalForm || modalForm.dataset.marketSubmitting === 'true') return;
       if (!requireExplicitCountryChoice()) {
         showMarketRequirementNotice();
         return;
       }

       const returnPath = preserveMarketReturnPath();
       modalForm.dataset.marketSubmitting = 'true';
       setModalSaveState(true);
       try {
         const countryCode = modalForm.querySelector('select[name="country_code"]')?.value?.toUpperCase();
         const languageCode = modalForm.querySelector('select[name="language_code"]')?.value;
         await migrateMenoPlayCart(countryCode, languageCode);
       } catch (error) {
         modalForm.dataset.marketSubmitting = '';
         setModalSaveState(false);
         showMarketMigrationError(error);
         return;
       }

       // Record the explicit market choice only after cart migration succeeds and
       // immediately before Shopify's native localization POST.
       markMarketSelectionComplete();

       // Let Shopify handle the localization POST and redirect. Using the native
       // form submission avoids a fetch/redirect race that made Save appear inert.
       if (returnPath) {
         modalForm.querySelector('input[name="return_to"]').value = returnPath;
         const countryCode = modalForm.querySelector('select[name="country_code"]')?.value;
         const languageCode = modalForm.querySelector('select[name="language_code"]')?.value;
         persistMarketReturnPathCookie(marketHomepagePath(countryCode, languageCode));
       } else if (marketGate.isActive()) {
         const returnToInput = modalForm.querySelector('input[name="return_to"]');
         if (returnToInput) returnToInput.value = stripMarketRequiredFromPath(returnToInput.value);
       }
       // The forced market marker stays in the current URL: it is only dropped by
       // the sanitized destination after a successful native navigation.
       const submitted = submitNativeLocalizationForm({
         form: modalForm,
         submitForm: (form) => HTMLFormElement.prototype.submit.call(form),
         onFailure: () => {
           modalForm.dataset.marketSubmitting = '';
           setModalSaveState(false);
           showMarketMigrationError(new Error('No pudimos aplicar el mercado seleccionado. Inténtalo de nuevo.'));
         }
       });
       if (!submitted) return;
     };

    modalForm?.addEventListener('submit', submitLocalizationForm);

    modalContinue?.addEventListener('click', () => {
      if (!requireExplicitCountryChoice()) {
        showMarketRequirementNotice();
        return;
      }
      setMarketSelectionStep('language');
    });
    modalBack?.addEventListener('click', () => setMarketSelectionStep('country'));
    modalForm?.querySelectorAll('input[name="eusari-country"]').forEach((control) => {
      control.addEventListener('change', () => {
        if (!requireExplicitCountryChoice()) return;
        setMarketSelectionStep('language');
      });
    });

    modalReset?.addEventListener('click', (event) => {
      event.preventDefault();
      window.EusariAccessibility?.reset();
      syncAccessibilityControls();
      announceAccessibilityUpdate('Preferencias de accesibilidad restablecidas.');
    });
    if (marketGate.isActive()) {
      // Forced arrival from the Menotest result: the selector opens without a
      // click, the remembered completion is ignored and a country has to be
      // chosen explicitly before the destination can be reached.
      applyForcedCountryState(countryRadios);
      modalClose?.setAttribute('disabled', 'disabled');
      modalClose?.setAttribute('aria-disabled', 'true');
      setModal(true);
      requestAnimationFrame(() => countryRadios[0]?.focus());
    } else {
      openMarketSelectionOnFirstVisit();
    }

    modalSave?.addEventListener('click', () => {
      if (!modalForm) {
        setModal(false, true);
        return;
      }
      if (!requireExplicitCountryChoice()) {
        showMarketRequirementNotice();
        return;
      }
      const locale = modalForm.querySelector('input[name="eusari-locale"]:checked');
      const country = modalForm.querySelector('input[name="eusari-country"]:checked');
       const localeSelect = modalForm.querySelector('select[name="language_code"]');
      const countrySelect = modalForm.querySelector('select[name="country_code"]');
       if (locale && localeSelect) {
         localeSelect.value = locale.value;
       }
       if (country && countrySelect) countrySelect.value = country.value;
       submitLocalizationForm();
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-eusari-section="header"]').forEach(init);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      marketSelectionStorageKey,
      marketReturnPathCookieKey,
      marketHomepagePath,
      marketReturnPath,
      encodeMarketReturnPath,
      readMarketReturnPathCookie,
      isLocalizedStorefrontPath,
      localizedMarketHomepagePath,
      rootMarketRedirectTarget,
      clearMarketReturnPathCookie,
      persistMarketReturnPathCookie,
      persistMarketSelection,
      readStoredMarketSelection,
      shouldAutoOpenMarketSelection,
      marketSelectionRequired,
      shouldForceMarketSelection,
      stripMarketRequiredParam,
      stripMarketRequiredFromPath,
      applyForcedCountryState,
      hasExplicitCountryChoice,
      createMarketSelectionGate,
      submitNativeLocalizationForm,
      isVisibleFocusable,
      marketSelectionSteps
    };
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
    else initAll();
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})();
